import React, { useEffect, useState } from 'react';
import { Shield, Users, Award, Target } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const About = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.35}px)`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(11,112,42,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.2)_1px,transparent_1px)] bg-[size:40px_40px]" style={{ transform: `translateY(${scrollY * 0.15}px)`, willChange: 'transform' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#014421]/30 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.about.title}</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">{t.about.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.about.mission}</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">{t.about.missionText1}</p>
              <p className="text-xl text-gray-300 leading-relaxed">{t.about.missionText2}</p>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800" alt="Cybersecurity" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.2}px) scale(${1 + scrollY * 0.0001})`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#014421]/20 to-black" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.about.values}</h2>
            <p className="text-xl text-gray-400">{t.about.valuesSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                <Shield className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.about.valuesList.security.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.about.valuesList.security.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                <Users className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.about.valuesList.team.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.about.valuesList.team.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                <Award className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.about.valuesList.excellence.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.about.valuesList.excellence.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                <Target className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.about.valuesList.results.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.about.valuesList.results.description}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-[#0B702A] mb-2">500+</div>
              <div className="text-gray-400">{t.about.stats.projects}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#0B702A] mb-2">15+</div>
              <div className="text-gray-400">{t.about.stats.experience}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#0B702A] mb-2">98%</div>
              <div className="text-gray-400">{t.about.stats.satisfaction}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#0B702A] mb-2">24/7</div>
              <div className="text-gray-400">{t.about.stats.support}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;